import { format, parseISO } from 'date-fns';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useUserEvents, type NameCount } from '@/hooks/useSuperadmin';
import { nf } from './utils';

const ALL = '__all__';

interface Props {
  tenantId: string;
  userId: string;
  days: number;
  day: string | null;
  actionType: string | null;
  actionTypes: NameCount[];
  onDay: (day: string | null) => void;
  onType: (type: string | null) => void;
  onClear: () => void;
}

/** Filterable, paged event timeline. Filters run on the server, so totals and paging cover every event in range. */
export const EventTimeline = ({ tenantId, userId, days, day, actionType, actionTypes, onDay, onType, onClear }: Props) => {
  const { events, total, loading, loadingMore, error, loadMore, hasMore } = useUserEvents(tenantId, userId, days, day, actionType);
  const filtered = !!day || !!actionType;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Activity timeline</CardTitle>
        <CardDescription>Select a day on the chart or an event type to filter. Event types and pages only; payloads are never shown.</CardDescription>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Select value={actionType ?? ALL} onValueChange={(v) => onType(v === ALL ? null : v)}>
            <SelectTrigger className="h-9 w-56" aria-label="Filter by event type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All event types</SelectItem>
              {actionTypes.map((t) => <SelectItem key={t.name} value={t.name}>{t.name.replace(/_/g, ' ')} ({nf(t.count)})</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Day</span>
            <input
              type="date" value={day ?? ''} onChange={(e) => onDay(e.target.value || null)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Filter by day (UTC)"
            />
          </label>
          {filtered && (
            <Button type="button" variant="ghost" size="sm" onClick={onClear}>
              <X className="me-1 h-3.5 w-3.5" aria-hidden="true" /> Clear filters
            </Button>
          )}
          <span className="ms-auto font-mono text-xs text-muted-foreground" aria-live="polite" data-testid="timeline-count">
            {loading ? 'Loading…' : `${nf(total)} event${total === 1 ? '' : 's'}${day ? ` on ${format(parseISO(day), 'd MMM yyyy')}` : ''}${actionType ? ` · ${actionType.replace(/_/g, ' ')}` : ''}`}
          </span>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {error && <p role="alert" className="mb-3 text-sm text-destructive">Could not load events: {error}</p>}
        {!loading && events.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">{filtered ? 'No events match these filters.' : 'No events in this period.'}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
                <th scope="col" className="py-2 text-start font-normal">When (UTC)</th>
                <th scope="col" className="py-2 text-start font-normal">Event</th>
                <th scope="col" className="py-2 text-start font-normal">Screen</th>
                <th scope="col" className="py-2 text-start font-normal">Page</th>
              </tr>
            </thead>
            <tbody>
              {events.map((r, i) => (
                <tr key={`${r.at}-${i}`} className="border-b border-border last:border-0">
                  <td className="whitespace-nowrap py-2 font-mono text-xs">{r.at.slice(0, 19).replace('T', ' ')}</td>
                  <td className="py-2 capitalize">{r.action_type.replace(/_/g, ' ')}</td>
                  <td className="py-2 font-mono text-xs">{r.view ?? ''}</td>
                  <td className="py-2 font-mono text-xs text-muted-foreground">{r.page_path ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {hasMore && (
          <div className="pt-3 text-center">
            <Button type="button" variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>
              {loadingMore && <Loader2 className="me-1 h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
              Load more ({nf(total - events.length)} remaining)
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
